/**
 * The FAQ — the questions, the answers, and nothing else.
 *
 * EVERY WORD HERE TRACES TO yedding-brain/BRAND-CONTEXT.md
 *   Each answer carries the section it came from in a comment above it. An
 *   answer with no source section does not belong in this file. Never add
 *   a fact that is not in that document — read §7 (claims policy) before
 *   changing one word, and §12 for the words to avoid.
 *
 * THE NEVER-PUBLISH LIST, IN SHORT (yedding-brain/BRAND-CONTEXT.md §7)
 *   No management fee percentage. No deposit amount. No example venue
 *   price. No promise of a percentage saved. Never say Yedding provides
 *   insurance or damage protection. Nothing about the refund policy. No
 *   guest-count threshold for the insurance rule. No ratings or review
 *   counts.
 *
 *   **80 is the only number allowed in this file.** Hosts keep 80% of the
 *   listed price, approved for publication on 2026-09-05. Every other
 *   figure — price, capacity, fee, deposit — is written in words or comes
 *   live from Firestore on the listing itself.
 *
 * WHY THE ANSWERS READ THE WAY THEY DO
 *   Each one leads with the direct answer, then the detail, and is 40-80
 *   words. It has to make sense quoted on its own, with no page around it,
 *   because that is how an answer engine will use it.
 *
 * ONE COPY OF THE WORDS
 *   src/components/Faq builds both the visible page and the FAQPage
 *   JSON-LD from this file. The two can never disagree, because there is
 *   only one copy of the text.
 *
 * See docs/aeo/AEO-LEDGER.md, task 10.
 */

const faq = {
  title: 'Frequently asked questions',
  intro:
    'Answers about booking a Yedding venue, and about listing one. If the answer you need is not here, email support@yedding.com.',
  groups: [
    {
      id: 'for-couples',
      heading: 'For couples and event planners',
      questions: [
        {
          id: 'own-vendors',
          question: 'Can I bring my own caterer, florist, and photographer?',
          // Source: BRAND-CONTEXT.md §2.1 — the hard platform rule, and §3, the
          // strongest single differentiator on the site.
          answer:
            'Yes, at every Yedding venue. You can hire any caterer, florist, photographer or other vendor you want. There is no approved-vendor list on any Yedding listing, ever. This is a hard platform rule rather than a preference: a venue that requires you to use its own vendors is not accepted onto the platform. The people at your event are the people you chose.'
        },
        {
          id: 'whole-day',
          question: 'Do I have to book in an eight-hour block?',
          // Source: BRAND-CONTEXT.md §2.2. "Many", never "all" — §7.
          answer:
            'Many Yedding venues are booked for the whole day, not in an eight-hour block. Traditional venues usually sell a block of hours, and setup and cleanup eat into the event itself. Booking the day instead means the schedule is yours. Terms vary from venue to venue, so check the individual listing before you book.'
        },
        {
          id: 'fees',
          question: 'What fees will I actually pay?',
          // Source: BRAND-CONTEXT.md §2.3 — the components by name only. The
          // management fee percentage and the deposit amount are never published.
          answer:
            'Everything is visible before you book. Your total is made up of the listed venue price, a management fee, and applicable tax.'
        },
        {
          id: 'overnight',
          question: 'Can we stay overnight at the venue?',
          // Source: BRAND-CONTEXT.md §2.5 — "some", and explain the two-day
          // mechanism, per §7.
          answer:
            'Some Yedding venues allow an overnight stay. The mechanism is simple: you book two days, and the day price is split to cover the overnight portion. In practice that means a couple can stay the night without paying an extra overnight fee on top of the two-day booking. Availability differs by venue, so check the listing.'
        },
        {
          id: 'all-inclusive',
          question: 'Is Yedding all-inclusive, or do I plan the wedding myself?',
          // Source: BRAND-CONTEXT.md §4, the approved publishable framing,
          // reproduced word for word. Do not soften it and do not add to it:
          // a reader who wants an all-inclusive package should self-select out
          // here rather than after they have booked.
          answer:
            'A Yedding venue is a blank canvas, and that freedom is the point. The listed price is for the event space itself, so the flowers, the catering and the planning stay your choice. You hire the people you actually want instead of the ones a venue assigns you. If you are set on an all-inclusive package where the venue handles everything, Yedding is probably not the right fit.'
        },
        {
          id: 'insurance',
          question: 'Do I need event insurance?',
          // Source: BRAND-CONTEXT.md §7, "Insurance — the correct wording".
          // Yedding provides none. Never state the guest-count threshold.
          answer:
            'Yedding does not provide insurance. Yedding requires the person booking to carry their own event insurance in two cases: a very large event, and any event where alcohol is served. If either applies to you, arrange the policy through your own insurer, in your own name, before the event. Yedding does not provide damage protection either, beyond your own policy.'
        },
        {
          id: 'price',
          question: 'Why are Yedding venues cheaper than traditional wedding venues?',
          // Source: BRAND-CONTEXT.md §2.4. No example price and no percentage
          // saved — §7. "Generally", never "only" — §3.
          answer:
            'Because the supply is different. Yedding listings are generally private estates, ranches, mountain properties and other characterful places rather than commercial event halls, and that non-traditional supply costs significantly less, often for a comparable or better property. Prices change every year, so Yedding shows the real current price on each listing instead of an example figure here.'
        },
        {
          id: 'event-types',
          question: 'What kinds of events can I book?',
          // Source: BRAND-CONTEXT.md §5 (event types served) and §4 (a Yedding
          // listing is booked to hold an event, not as a place to stay).
          answer:
            'Weddings and receptions, and a good deal more. Yedding venues host corporate retreats and off-sites, networking events, corporate training, birthday parties, dance parties and quinceañeras. Every Yedding listing is booked specifically to hold an event, rather than as a place to stay. That is what the venue is offered for, and what the host is expecting.'
        },
        {
          id: 'capacity',
          question: 'How many guests can a Yedding venue hold?',
          // Source: BRAND-CONTEXT.md §5 and §6. The real figures are internal
          // only, so the range is described in words. Task 15 shows the real
          // capacity of a real listing, computed live.
          answer:
            'The range is wide. Some Yedding venues suit a small, intimate gathering of a few dozen people; others are large estates and ranches with the outdoor space for several hundred. Very large events are outside what Yedding serves. Each listing shows its own capacity, and the venue search can filter by guest count, so compare the real figures there.'
        },
        {
          id: 'venue-review',
          question: 'Are the venues checked before they are listed?',
          // Source: BRAND-CONTEXT.md §7 (safe to publish: every venue is
          // personally reviewed and approved) and §6 (photography and film).
          // Never list what is checked during review — §7.
          answer:
            'Yes. Every venue on Yedding is personally reviewed and approved before it appears on the site. A listing is not self-service: a person checks it first. Yedding is also photographing and filming venues, so you can judge a property properly from the listing rather than spending your weekends touring places that were never right.'
        }
      ]
    },
    {
      id: 'for-hosts',
      heading: 'For venue hosts',
      questions: [
        {
          id: 'host-earnings',
          question: 'How much do I earn hosting events?',
          // Source: BRAND-CONTEXT.md §6. The 80/20 split is approved for
          // publication (2026-09-05). The management fee percentage is not.
          answer:
            'Hosts keep 80% of the listed price. Yedding keeps the management fee, and that is the whole arrangement. Yedding brings the demand, runs the marketing and generally handles the tours, so hosting is extra income from property you already own rather than a venue business you have to build and run.'
        },
        {
          id: 'host-tours',
          question: 'Do I have to run the tours myself?',
          // Source: BRAND-CONTEXT.md §6. "Generally" — Yedding handles tours,
          // and a host who prefers to run their own can.
          answer:
            'No. Yedding generally handles venue tours, and a host who prefers to run their own can do that instead. Yedding is also photographing and filming venues, so bookers can see the property properly before they visit. That cuts the number of tours, and means the people who do walk your property are serious and ready to book.'
        },
        {
          id: 'host-property',
          question: 'What kind of property can be listed?',
          // Source: BRAND-CONTEXT.md §6 (property types, and who hosts are) and
          // §3 (the two hard rules; property type is not one of them).
          answer:
            'Large, attractive private property with plenty of usable outdoor space. Private homes offering the yard or the whole property, ranches, private estates and mountain properties are all on the platform. Two rules are hard: the property must be unique, and it must allow any vendor. Property type itself is not a rule, so a distinctive space that meets both can list.'
        },
        {
          id: 'host-advertising',
          question: 'Do I have to advertise?',
          // Source: BRAND-CONTEXT.md §6 — Yedding brings the demand; hosts do
          // not want to advertise, market or find bookers themselves.
          answer:
            'No. Yedding brings the demand, so a host does not advertise, market, or go looking for bookers. Yedding photographs and films the property, lists it, and generally runs the tours. Your side of it is the property itself and the work of hosting the event on the day. Hosts keep 80% of the listed price.'
        }
      ]
    }
  ]
};

/** Every question, both groups, in page order. Used by the JSON-LD builder. */
export const allQuestions = () => faq.groups.flatMap((group) => group.questions);

export default faq;
